/* Bespoke campaign UI. Commerce submission remains in Dawn's ProductForm. */
(() => {
  const make = (tag, className, text) => {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined) node.textContent = text;
    return node;
  };
  const normalizeLabel = title => title.replace(/(\d)Pack\b/g, '$1 Pack').replace(/ PDQ$/, '');
  const descriptionLines = markup => {
    const documentFragment = new DOMParser().parseFromString(markup || '', 'text/html');
    documentFragment.querySelectorAll('script, style, iframe, object, embed, form, svg').forEach(node => node.remove());
    const blocks = [...documentFragment.body.querySelectorAll('p, li, h2, h3')]
      .map(node => node.textContent.replace(/\s+/g, ' ').trim()).filter(Boolean);
    return blocks.length ? blocks : [documentFragment.body.textContent.replace(/\s+/g, ' ').trim()].filter(Boolean);
  };
  const packCount = title => Number(title.match(/^(1|3|6|12)\s*Pack(?:\s+PDQ)?$/i)?.[1]) || 0;
  const packOffers = variants => {
    const counts = variants.map(item => packCount(item.title));
    const single = variants.find((item, index) => counts[index] === 1);
    const triple = variants.find((item, index) => counts[index] === 3);
    if (variants.length !== 4 || !single || !triple || single.price <= 0 || triple.price <= 0 || counts.some(count => !count) || new Set(counts).size !== counts.length ||
      variants.some(item => !Number.isSafeInteger(item.price) || item.price < 0)) return null;
    return new Map(variants.map((item, index) => {
      const count = counts[index];
      // These are alternative purchases, not Shopify compare-at / historical prices.
      const comparison = count === 3 ? single.price * 3 : count > 3 ? triple.price * (count / 3) : 0;
      const saving = count > 1 ? Math.max(0, comparison - item.price) : 0;
      return [item.id, { count, comparison, saving }];
    }));
  };

  class MojisLab extends HTMLElement {
    connectedCallback() {
      const initial = this.querySelector('[data-product]');
      if (!initial || this.lifecycle) return;
      this.lifecycle = new AbortController();
      this.active = JSON.parse(initial.textContent);
      this.products = new Map([[this.active.handle, this.active]]);
      this.productRequests = new Map();
      this.collectionHandles = new Set([this.active.handle, ...[...this.querySelectorAll('[data-product-handle]')].map(button => button.dataset.productHandle)]);
      const requestedId = new URLSearchParams(location.search).get('variant') || this.querySelector('.mojis-form [name="id"]').value;
      this.variant = this.active.variants.find(item => String(item.id) === requestedId) || this.active.variants.find(item => item.available) || this.active.variants[0];
      this.base = this.dataset.root || window.Shopify.routes.root;
      this.moneyPattern = JSON.parse(this.querySelector('[data-money-format]').textContent);
      this.form = this.querySelector('.mojis-form');
      this.media = this.querySelector('[data-gallery]');
      this.requestId = 0;
      const opts = { signal: this.lifecycle.signal };
      this.addEventListener('click', this.handleClick.bind(this), opts);
      window.addEventListener('popstate', () => this.restoreRoute(), opts);
      this.addEventListener('keydown', event => {
        const current = event.target.closest('[data-variant-id][role="radio"]');
        if (!current || current.disabled || this.busy || this.purchasing || !['ArrowDown', 'ArrowRight', 'ArrowUp', 'ArrowLeft', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        const choices = [...this.querySelectorAll('[data-variant-id][role="radio"]')].filter(button => !button.disabled);
        const direction = ['ArrowDown', 'ArrowRight'].includes(event.key) ? 1 : -1;
        const index = event.key === 'Home' ? 0 : event.key === 'End' ? choices.length - 1 : (choices.indexOf(current) + direction + choices.length) % choices.length;
        this.selectVariant(choices[index].dataset.variantId);
        this.writeVariantRoute();
        choices[index].focus();
      }, opts);
      this.form.elements.quantity.addEventListener('change', () => this.syncControls(), opts);
      this.media.addEventListener('scroll', this.trackMedia.bind(this), { ...opts, passive: true });
      this.media.addEventListener('keydown', event => {
        if (event.target !== this.media || !['ArrowRight', 'ArrowLeft'].includes(event.key)) return;
        event.preventDefault();
        this.navigateMedia(this.mediaIndex() + (event.key === 'ArrowRight' ? 1 : -1));
      }, opts);
      document.addEventListener('click', event => {
        const menu = this.querySelector('.mojis-menu');
        if (!menu.contains(event.target)) menu.open = false;
      }, opts);
      this.addEventListener('keydown', event => {
        if (event.key === 'Escape') this.querySelector('.mojis-menu').open = false;
      }, opts);
      this.selectVariant(this.variant?.id);
      if (this.dataset.nativeProduct !== 'true') this.restoreRoute();
    }

    disconnectedCallback() {
      this.lifecycle?.abort();
      this.productRequests?.forEach(request => request.controller.abort());
      cancelAnimationFrame(this.mediaFrame);
      this.lifecycle = null;
    }

    money(cents) {
      const formats = {
        amount: [2, ',', '.'], amount_no_decimals: [0, ',', '.'],
        amount_with_comma_separator: [2, '.', ','], amount_no_decimals_with_comma_separator: [0, '.', ','],
        amount_with_space_separator: [2, ' ', ','], amount_no_decimals_with_space_separator: [0, ' ', ','],
        amount_with_apostrophe_separator: [2, "'", '.']
      };
      const match = this.moneyPattern.match(/\{\{\s*(\w+)\s*\}\}/);
      if (!match || !formats[match[1]]) return new Intl.NumberFormat(document.documentElement.lang, { style: 'currency', currency: this.dataset.currency }).format(cents / 100);
      const [precision, grouping, decimal] = formats[match[1]];
      const [whole, fraction] = (cents / 100).toFixed(precision).split('.');
      const value = whole.replace(/\B(?=(\d{3})+(?!\d))/g, grouping) + (fraction ? decimal + fraction : '');
      const plain = make('span');
      plain.innerHTML = this.moneyPattern.replace(match[0], value);
      return plain.textContent;
    }

    handleClick(event) {
      const control = event.target.closest('button');
      if (!control || control.disabled) return;
      if (control.hasAttribute('data-product-handle')) this.activate(control.dataset.productHandle, { history: 'push' });
      if (control.hasAttribute('data-variant-id') && !this.busy && !this.purchasing) { this.selectVariant(control.dataset.variantId); this.writeVariantRoute(); }
      if (control.hasAttribute('data-quantity-delta')) this.quantity(Number(this.form.elements.quantity.value) + Number(control.dataset.quantityDelta));
      if (control.hasAttribute('data-media-index')) this.navigateMedia(Number(control.dataset.mediaIndex));
      if (control.hasAttribute('data-media-step')) this.navigateMedia(this.mediaIndex() + Number(control.dataset.mediaStep));
    }

    quantity(value) {
      const rule = this.variant?.quantity_rule || {};
      const min = Number.isSafeInteger(rule.min) && rule.min > 0 ? rule.min : 1;
      const max = Number.isSafeInteger(rule.max) && rule.max > 0 ? rule.max : Number.MAX_SAFE_INTEGER;
      const step = Number.isSafeInteger(rule.increment) && rule.increment > 0 ? rule.increment : 1;
      const desired = Number.isSafeInteger(value) ? value : min;
      this.form.elements.quantity.value = Math.min(max, Math.max(min, min + Math.round((desired - min) / step) * step));
      this.syncControls();
    }

    syncControls() {
      const locked = !!(this.busy || this.purchasing);
      this.querySelector('[data-atc]').disabled = locked || !this.variant?.available;
      this.querySelector('[data-checkout]').disabled = locked || !this.variant?.available;
      this.querySelector('[data-atc-label]').textContent = this.busy ? 'Loading…' : this.variant?.available ? 'Add to Cart' : 'Sold Out';
      this.querySelector('[data-checkout]').textContent = this.purchasing && this.checkout ? 'Adding…' : 'Buy Now';
      this.form.elements.quantity.disabled = locked;
      const minQuantity = Number.isSafeInteger(this.variant?.quantity_rule?.min) && this.variant.quantity_rule.min > 0 ? this.variant.quantity_rule.min : 1;
      this.querySelectorAll('[data-quantity-delta]').forEach(button => { button.disabled = locked || (button.dataset.quantityDelta === '-1' && Number(this.form.elements.quantity.value) <= minQuantity); });
      this.querySelectorAll('[data-variant-id]').forEach(button => {
        const item = this.active.variants.find(variant => String(variant.id) === button.dataset.variantId);
        button.disabled = locked || !item?.available;
      });
      const radios = [...this.querySelectorAll('[data-variant-id][role="radio"]')];
      const tabStop = radios.find(button => !button.disabled && button.getAttribute('aria-checked') === 'true') || radios.find(button => !button.disabled);
      radios.forEach(button => { button.tabIndex = button === tabStop ? 0 : -1; });
      this.querySelectorAll('[data-product-handle]').forEach(button => { button.disabled = !!this.purchasing; });
    }

    selectVariant(id) {
      this.variant = this.active.variants.find(item => String(item.id) === String(id));
      this.form.elements.id.value = this.variant?.id || '';
      const quantityRule = this.variant?.quantity_rule || {};
      this.form.elements.quantity.min = Number.isSafeInteger(quantityRule.min) && quantityRule.min > 0 ? quantityRule.min : 1;
      this.form.elements.quantity.step = Number.isSafeInteger(quantityRule.increment) && quantityRule.increment > 0 ? quantityRule.increment : 1;
      if (Number.isSafeInteger(quantityRule.max) && quantityRule.max > 0) this.form.elements.quantity.max = quantityRule.max;
      else this.form.elements.quantity.removeAttribute('max');
      if (Number(this.form.elements.quantity.value) < Number(this.form.elements.quantity.min)) this.form.elements.quantity.value = this.form.elements.quantity.min;
      this.querySelector('[data-price]').textContent = this.variant ? this.money(this.variant.price) : 'Unavailable';
      const compare = this.querySelector('[data-compare]');
      compare.hidden = !(this.variant?.compare_at_price > this.variant?.price);
      compare.textContent = compare.hidden ? '' : this.money(this.variant.compare_at_price);
      this.querySelectorAll('[data-variant-id]').forEach(button => button.setAttribute(button.getAttribute('role') === 'radio' ? 'aria-checked' : 'aria-pressed', String(button.dataset.variantId === String(this.variant?.id))));
      this.querySelector('mojis-purchase-form')?.handleErrorMessage?.();
      this.syncControls();
    }

    writeRoute(handle, method) {
      const url = new URL(location.href);
      if (url.searchParams.get('product') === handle && url.searchParams.getAll('product').length === 1) return;
      url.searchParams.set('product', handle);
      window.history[`${method}State`](window.history.state, '', url);
    }

    writeVariantRoute() {
      if (!this.variant?.id) return;
      const url = new URL(location.href);
      if (url.searchParams.get('variant') === String(this.variant.id) && url.searchParams.getAll('variant').length === 1) return;
      url.searchParams.set('variant', String(this.variant.id));
      window.history.replaceState(window.history.state, '', url);
    }

    async restoreRoute() {
      if (this.purchasing) { this.routePending = true; return; }
      const url = new URL(location.href);
      const requested = url.searchParams.get('product');
      const handle = this.collectionHandles.has(requested) ? requested : this.dataset.defaultHandle;
      if (requested !== null && (requested !== handle || url.searchParams.getAll('product').length > 1)) this.writeRoute(handle, 'replace');
      const activation = this.activate(handle, { variantId: url.searchParams.get('variant') });
      const request = this.requestId;
      const activated = await activation;
      if (request !== this.requestId || !this.isConnected) return;
      if (!activated) {
        const fallback = this.activate(this.dataset.defaultHandle);
        const fallbackRequest = this.requestId;
        await fallback;
        if (fallbackRequest !== this.requestId || !this.isConnected) return;
        this.writeRoute(this.active.handle, 'replace');
      }
      this.removeAttribute('data-entry-pending');
    }

    loadProduct(handle) {
      if (this.products.has(handle)) return Promise.resolve(this.products.get(handle));
      if (this.productRequests.has(handle)) return this.productRequests.get(handle).promise;
      const controller = new AbortController();
      const promise = fetch(`${this.base}products/${encodeURIComponent(handle)}.js`, { signal: controller.signal, headers: { Accept: 'application/json' }, cache: 'no-store' })
        .then(async result => {
          if (!result.ok) throw new Error('Unable to load this product. Please try again.');
          const product = await result.json();
          if (!product.id || product.handle !== handle || !Array.isArray(product.variants) || !product.variants.length) throw new Error('This product is unavailable.');
          this.products.set(handle, product);
          return product;
        })
        .finally(() => this.productRequests.delete(handle));
      this.productRequests.set(handle, { controller, promise });
      return promise;
    }

    async activate(handle, options = {}) {
      if (this.purchasing || !this.collectionHandles.has(handle)) return false;
      if (handle === this.active.handle && !this.busy) {
        if (this.active.variants.some(item => String(item.id) === options.variantId)) this.selectVariant(options.variantId);
        if (options.history) this.writeRoute(handle, options.history);
        if (options.history) this.writeVariantRoute();
        return true;
      }
      const request = ++this.requestId;
      this.busy = true;
      this.querySelector('[data-pdp]').setAttribute('aria-busy', 'true');
      this.querySelector('[data-status]').textContent = '';
      this.syncControls();
      try {
        const next = await this.loadProduct(handle);
        if (request !== this.requestId || !this.isConnected) return;
        this.active = next;
        this.dataset.commerceMode = next.handle === 'mojis-mystery-dumpling-squishy-toy' ? 'dumplings' : 'standard';
        this.querySelector('[data-product]').textContent = JSON.stringify(next);
        this.querySelector('[data-title]').textContent = next.title;
        const details = descriptionLines(next.description);
        this.querySelector('[data-description]').textContent = details[0] || '';
        const detailsSection = this.querySelector('[data-product-details]');
        if (detailsSection) {
          detailsSection.hidden = next.handle === 'mojis-mystery-dumpling-squishy-toy' || !details.length;
          detailsSection.querySelector('[data-details-title]').textContent = next.title;
          detailsSection.querySelector('[data-details-body]').replaceChildren(...details.map(line => make('p', '', line)));
        }
        const dumplingsFaq = this.querySelector('[data-dumplings-faq]');
        if (dumplingsFaq) dumplingsFaq.hidden = next.handle !== 'mojis-mystery-dumpling-squishy-toy';
        this.rebuildMedia();
        this.rebuildPacks();
        this.form.elements.quantity.value = 1;
        this.form.querySelector('[name="product-id"]')?.setAttribute('value', next.id);
        this.selectVariant((next.variants.find(item => String(item.id) === options.variantId) || next.variants.find(item => item.available) || next.variants[0])?.id);
        this.querySelectorAll('[data-product-handle]').forEach(button => button.setAttribute('aria-pressed', String(button.dataset.productHandle === handle)));
        this.updateRating();
        this.dispatchEvent(new CustomEvent('mojis:product-change', { bubbles: true, detail: { product: next } }));
        if (options.history) this.writeRoute(handle, options.history);
        if (options.history) this.writeVariantRoute();
        return true;
      } catch (error) {
        if (request === this.requestId && error.name !== 'AbortError') this.querySelector('[data-status]').textContent = error.message;
        return false;
      } finally {
        if (request === this.requestId) {
          this.busy = false;
          this.querySelector('[data-pdp]').setAttribute('aria-busy', 'false');
          this.removeAttribute('data-entry-pending');
          this.syncControls();
        }
      }
    }

    rebuildPacks() {
      const variants = this.active.variants;
      const offers = this.dataset.commerceMode === 'dumplings' && this.active.handle === this.dataset.defaultHandle ? packOffers(variants) : null;
      const merchandising = !!offers;
      const packs = this.querySelector('[data-packs]');
      packs.hidden = variants.length === 1 && variants[0].title === 'Default Title';
      packs.classList.toggle('mojis-packs--offers', merchandising);
      this.querySelector('[data-options-label]').textContent = this.active.handle === this.dataset.defaultHandle ? 'Pack Size:' : 'Choose an option:';
      const fragment = document.createDocumentFragment();
      const ordered = offers ? [...variants].sort((a, b) => offers.get(a.id).count - offers.get(b.id).count) : variants;
      ordered.forEach(item => {
        const button = make('button'); button.type = 'button'; button.dataset.variantId = item.id;
        const offer = offers?.get(item.id);
        if (offer) {
          button.dataset.packCount = offer.count;
          button.setAttribute('role', 'radio');
          const name = make('span', 'mojis-pack__name');
          name.append(make('strong', '', normalizeLabel(item.title)));
          const badge = offer.count === 3 ? 'Starter' : offer.count === 6 ? 'Our Pick' : offer.count === 12 ? 'Best Value' : '';
          if (badge) name.append(make('span', 'mojis-pack__badge', badge));
          const check = make('span', 'mojis-pack__check', '✓'); check.setAttribute('aria-hidden', 'true');
          const details = make('span', 'mojis-pack__prices');
          details.append(make('span', 'mojis-pack__price', this.money(item.price)));
          if (offer.saving > 0) {
            const basis = offer.count === 3 ? 'Cost of three singles' : offer.count === 6 ? 'Cost of two 3 Packs' : 'Cost of four 3 Packs';
            details.append(make('span', 'visually-hidden', `${basis}: ${this.money(offer.comparison)}`));
            const comparison = make('s', 'mojis-pack__comparison', this.money(offer.comparison)); comparison.setAttribute('aria-hidden', 'true');
            details.append(comparison, make('small', 'mojis-pack__saving', `You save ${this.money(offer.saving)}`));
          }
          if (!item.available) details.append(make('small', '', 'Sold out'));
          button.append(name, details, check);
        } else {
          button.append(make('strong', '', normalizeLabel(item.title)), make('span', '', this.money(item.price)));
          if (!item.available) button.append(make('small', '', 'Sold out'));
          else if (item.compare_at_price > item.price) button.append(make('small', '', `Save ${this.money(item.compare_at_price - item.price)}`));
        }
        fragment.append(button);
      });
      const choices = this.querySelector('[data-variants]');
      if (offers) {
        choices.setAttribute('role', 'radiogroup');
        choices.setAttribute('aria-labelledby', this.querySelector('[data-options-label]').id);
      } else {
        choices.removeAttribute('role');
        choices.removeAttribute('aria-labelledby');
      }
      choices.replaceChildren(fragment);
    }

    resized(src, width) {
      const url = new URL(src, location.origin);
      if (url.hostname.endsWith('shopify.com')) url.searchParams.set('width', width);
      return url.href;
    }

    rebuildMedia() {
      this.media.querySelectorAll('video').forEach(video => video.pause());
      let items = this.active.media?.length ? [...this.active.media] : (this.active.images || []).map(src => ({ media_type: 'image', src }));
      if (this.active.handle === this.dataset.defaultHandle && this.active.featured_image) {
        const featured = new URL(this.active.featured_image, location.origin).pathname;
        const index = items.findIndex(item => item.media_type === 'image' && new URL(item.src || item.preview_image?.src, location.origin).pathname === featured);
        if (index > 0) items.unshift(items.splice(index, 1)[0]);
      }
      if (!items.length) items = [{ media_type: 'missing' }];
      const slides = document.createDocumentFragment();
      const thumbs = document.createDocumentFragment();
      items.forEach((item, index) => {
        const slide = make('div', 'mojis-gallery__slide');
        const source = item.src || item.preview_image?.src;
        if (item.media_type === 'video') {
          const video = make('video'); video.controls = true; video.playsInline = true; video.preload = 'none';
          if (item.preview_image?.src) video.poster = this.resized(item.preview_image.src, 800);
          item.sources?.forEach(mediaSource => { const node = make('source'); node.src = mediaSource.url; node.type = mediaSource.mime_type; video.append(node); });
          slide.append(video);
        } else if (item.media_type === 'external_video') {
          const link = make('a', '', 'Watch product video'); link.target = '_blank'; link.rel = 'noopener';
          link.href = item.host === 'youtube' ? `https://www.youtube.com/watch?v=${encodeURIComponent(item.external_id)}` : `https://vimeo.com/${encodeURIComponent(item.external_id)}`;
          slide.append(link);
        } else if (item.media_type === 'model' && customElements.get('model-viewer')) {
          const model = make('model-viewer'); model.src = item.sources?.find(entry => entry.format === 'glb')?.url || '';
          model.setAttribute('camera-controls', ''); model.setAttribute('alt', item.alt || this.active.title); slide.append(model);
        } else if (source) {
          const image = make('img'); image.src = this.resized(source, 1200);
          image.srcset = [400, 600, 800, 1200].map(width => `${this.resized(source, width)} ${width}w`).join(','); image.sizes = '(max-width: 767px) 100vw, 600px';
          if (!index) image.fetchPriority = 'high';
          image.alt = item.alt || this.active.title; image.width = item.width || 1000; image.height = item.height || 1000; image.loading = index ? 'lazy' : 'eager'; slide.append(image);
        } else slide.textContent = 'Product image unavailable';
        const thumb = make('button'); thumb.type = 'button'; thumb.dataset.mediaIndex = index;
        thumb.setAttribute('aria-label', `Show media ${index + 1}`); thumb.setAttribute('aria-pressed', String(index === 0));
        if (source) { const image = make('img'); image.src = this.resized(item.preview_image?.src || source, 180); image.alt = ''; image.width = 180; image.height = 180; image.loading = 'lazy'; thumb.append(image); }
        else thumb.textContent = String(index + 1);
        slides.append(slide); thumbs.append(thumb);
      });
      this.media.replaceChildren(slides); this.querySelector('[data-thumbs]').replaceChildren(thumbs);
      this.media.scrollLeft = 0; this.querySelector('[data-thumbs]').scrollLeft = 0;
    }

    mediaIndex() { return Math.round(this.media.scrollLeft / this.media.clientWidth); }
    navigateMedia(index) {
      const count = this.media.children.length;
      this.media.scrollTo({ left: ((index + count) % count) * this.media.clientWidth, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
    }
    trackMedia() {
      cancelAnimationFrame(this.mediaFrame);
      this.mediaFrame = requestAnimationFrame(() => {
        const active = this.mediaIndex();
        const rail = this.querySelector('[data-thumbs]');
        [...rail.children].forEach((thumb, index) => thumb.setAttribute('aria-pressed', String(index === active)));
        const thumb = rail.children[active];
        if (thumb) {
          const delta = thumb.getBoundingClientRect().left - rail.getBoundingClientRect().left;
          if (delta < 0 || delta + thumb.offsetWidth > rail.clientWidth) rail.scrollLeft += delta;
        }
        [...this.media.children].forEach((slide, index) => { if (index !== active) slide.querySelector('video')?.pause(); });
      });
    }

    updateRating() {
      const host = this.querySelector('.mojis-rating');
      const card = [...this.querySelectorAll('[data-product-handle]')].find(item => item.dataset.productHandle === this.active.handle);
      host.replaceChildren(); host.hidden = !(Number(card?.dataset.raters) > 0);
      if (host.hidden) return;
      const rating = make('div', 'loox-rating'); rating.dataset.id = this.active.id;
      rating.dataset.rating = card.dataset.rating; rating.dataset.raters = card.dataset.raters;
      host.append(rating); // Existing Loox embed observes newly inserted widgets.
    }

    finishPurchase(message) {
      this.purchasing = false;
      this.checkout = false;
      this.querySelector('[data-status]').textContent = message;
      this.syncControls();
      if (this.routePending) { this.routePending = false; this.restoreRoute(); }
    }
  }

  if (!customElements.get('mojis-squish-lab')) customElements.define('mojis-squish-lab', MojisLab);

  customElements.whenDefined('product-form').then(() => {
    if (customElements.get('mojis-purchase-form')) return;
    class MojisPurchaseForm extends customElements.get('product-form') {
      async onSubmitHandler(event) {
        event.preventDefault();
        const lab = this.closest('mojis-squish-lab');
        if (!lab || lab.busy || lab.purchasing) return;
        if (lab.dataset?.brand === 'mojis') this.cart = null;
        const checkout = !!event.submitter?.hasAttribute('data-checkout');
        let prepared;
        try {
          prepared = window.MojisPurchase.prepare({ variant: lab.variant, variants: lab.active.variants, formData: new FormData(this.form) });
          if (checkout) window.MojisPurchase.checkoutUrl(lab.base, location.origin);
        } catch (error) {
          this.handleErrorMessage(error.message);
          lab.querySelector('[data-status]').textContent = error.message;
          return;
        }
        lab.checkout = checkout;
        lab.purchasing = true;
        lab.querySelector('[data-status]').textContent = '';
        this.handleErrorMessage();
        lab.syncControls();
        try {
          if (this.cart) {
            prepared.formData.append('sections', this.cart.getSectionsToRender().map(section => section.id));
            prepared.formData.append('sections_url', location.pathname);
          }
          const result = await window.MojisPurchase.submit({ url: window.routes.cart_add_url, prepared });
          if (!lab.isConnected) return;
          let drawerReady = !this.cart;
          if (this.cart) {
            drawerReady = !!result.sections && this.cart.getSectionsToRender().every(section => typeof result.sections[section.id] === 'string' && result.sections[section.id].trim());
            if (drawerReady) {
              try { this.cart.renderContents(result); } catch (error) { drawerReady = false; console.warn('Cart presentation did not update', error); }
            }
          }
          try {
            if (typeof publish === 'function') Promise.resolve(publish(PUB_SUB_EVENTS.cartUpdate, { source: 'mojis-purchase-form', productVariantId: prepared.id, cartData: result })).catch(error => console.warn('Cart listeners did not update', error));
          } catch (error) { console.warn('Cart listeners did not update', error); }
          if (checkout) {
            location.assign(window.MojisPurchase.checkoutUrl(lab.base, location.origin));
          } else {
            if (lab.dataset?.brand === 'mojis') {
              const bag = lab.querySelector('mojis-cart-drawer');
              try { drawerReady = !!(await bag?.open()); } catch (error) { drawerReady = false; console.warn('Cart review did not open', error); }
            }
            lab.finishPurchase(drawerReady ? 'Added to your cart!' : 'Added to your cart. Open your bag to review.');
          }
        } catch (error) {
          this.handleErrorMessage(error.message);
          lab.finishPurchase(error.message);
        }
      }
    }
    customElements.define('mojis-purchase-form', MojisPurchaseForm);
  });
})();
